import {
  WizardBannerContent,
  WizardMainContent,
  WizardPageAndStepContent,
  WizardSidebarContent,
  WizardStepsContent,
} from '../container/lensWizard/types';
import { ISiteMap } from '../services/content';
import { safeGet } from './objectUtils';
import { InternalLinks } from '../common/enums/common';

import {
  unwrapAboutBrand,
  unwrapActionTray,
  unwrapAlertMessage,
  unwrapBanner,
  unwrapBenefitTables,
  unwrapBenefitsMarketingBlock,
  unwrapCard,
  unwrapCarousel,
  unwrapCheckout,
  unwrapContactUs,
  unwrapDefaultMedicineImages,
  unwrapDiscount,
  unwrapFAQVirtualAssistant,
  unwrapIngredientsList,
  unwrapMarketingGrid,
  unwrapMarketingTiles,
  unwrapMarketingTilesSpacing,
  unwrapMedicineSKUImages,
  unwrapNavigation,
  unwrapProductInfo,
  unwrapRebates,
  unwrapSavingsPlan,
  unwrapSiteLogo,
  unwrapTab,
  unwrapTiles,
  upwrapOpticalBenefits,
  upwrapOurGuarantees,
  upwrapStoreAvailability,
} from './unwrapCSComponentModels';

export default function unwrapContentStackResponse(
  response: any = [],
  contentType?: string,
  all = false,
  SITE_MAP?: ISiteMap,
) {
  const allContent = response[0] || [];
  const max = all ? allContent.length : 1;
  const pageContents = [];
  SITE_MAP = SITE_MAP || {};
  for (let i = 0; i < max; i++) {
    let pageContent: any = {};
    const content = allContent[i];

    switch (contentType) {
      case 'page':
        pageContent = unwrapGlobalPageContent(content, SITE_MAP);
        break;
      case 'user_account':
        pageContent = unwrapAccountPageContent(content, SITE_MAP);
        break;
      case 'cart_page':
        pageContent = unwrapCartPageContent(content, SITE_MAP);
        break;
      case 'order_history':
        pageContent = unwrapOrderHistory(content, SITE_MAP);
        break;
      case 'contact_us':
        pageContent = unwrapContactUs(content, SITE_MAP);
        break;
      case 'plp_page':
        pageContent = unwrapPLPContent(content, SITE_MAP);
        break;
      case 'pdp_page':
        pageContent = unwrapPDPContent(content, SITE_MAP);
        break;
      case 'contacts_pdp_page':
        pageContent = unwrapPDPContactContent(content, SITE_MAP);
        break;
      case 'find_location_page':
        pageContent = unwrapFindLocation(content, SITE_MAP);
        break;
      case 'article_page':
        pageContent = unwrapArticlePage(content, SITE_MAP);
        break;
      case 'faqs_page':
        pageContent = unwrapFaqPage(content, SITE_MAP);
        break;
      case 'eyeglass_lens_wizard_page':
        pageContent = unwrapLensWizardPageContent(content, SITE_MAP);
        break;
      case 'cancel_order':
        pageContent = unwrapCancelOrderPageContent(content, SITE_MAP);
        break;
      default:
        pageContent = content || {};
        break;
    }
    pageContent.marketingGrid = unwrapMarketingGrid(content?.marketing_grid, SITE_MAP);
    pageContent.pageTitle = content?.page_name?.title || 'Home';
    pageContent.seo = {
      description: content?.seo?.meta_description || '',
      keywords: content?.seo?.meta_keywords || '',
    };
    pageContents.push(pageContent);
  }
  if (all) {
    return pageContents;
  }
  return pageContents[0];
}

//if CT is page
function unwrapGlobalPageContent(content: any, SITE_MAP: ISiteMap) {
  return {
    homeCarousel: unwrapCarousel(content?.carousel, SITE_MAP),
    actionTray: unwrapActionTray(content?.action_tray, SITE_MAP),
    vaFAQ: unwrapFAQVirtualAssistant(content, SITE_MAP),
  };
}

function unwrapAccountPageContent(content: any, SITE_MAP: ISiteMap) {
  return {
    welcomeMessage: content?.welcome_message,
    tabItems: unwrapTab(content?.tab, SITE_MAP),
    benefitTables: content?.benefit_tables ? unwrapBenefitTables(content?.benefit_tables) : {},
    savingsPlan: unwrapSavingsPlan(content?.protection_and_savings_plan),
    orderHistory: unwrapOrderHistory(content?.order_history, SITE_MAP),
  };
}

const unwrapPLPContent = (content: any, SITE_MAP: ISiteMap) => {
  return {
    banner: content?.banner ? unwrapBanner(content?.banner, SITE_MAP) : undefined,
    marketingTiles: content?.product_grid
      ? unwrapMarketingTiles(content?.product_grid, SITE_MAP)
      : [],
    marketingTilesSpacing: content?.product_grid
      ? unwrapMarketingTilesSpacing(content?.product_grid)
      : undefined,
  };
};

const unwrapPDPContent = (content: any, SITE_MAP: ISiteMap) => {
  content = mapPDPContent(content);
  const ourGuarantees = upwrapOurGuarantees(content, SITE_MAP);
  const storeAvailability = upwrapStoreAvailability(content);
  const opticalBenefits = upwrapOpticalBenefits(content, SITE_MAP);
  const aboutBrands = unwrapAboutBrand(content);
  const aboutBrandType = safeGet(content, 'about_the_brand', {});
  const rebates = unwrapRebates(content, SITE_MAP);
  const discount = unwrapDiscount(content);
  const benefitTables = unwrapBenefitTables(content?.benefit_tables);
  const attributeLabels = unwrapAttributeLabels(content);
  const productDetailsTitle = content.product_details_title;

  return {
    ourGuarantees,
    storeAvailability,
    opticalBenefits,
    aboutBrands,
    rebates,
    discount,
    benefitTables,
    attributeLabels,
    productDetailsTitle,
    aboutBrandType: {
      title: aboutBrandType.title,
      isEnabled: aboutBrandType.is_enabled,
    },
  };
};

const mapPDPContent = (content: any) => {
  content = {
    optical_benefits: safeGet(content, 'product_features.optical_benefits', []),
    attribute_labels: safeGet(content, 'product_info.attribute_labels', {}),
    product_details_title: safeGet(content, 'product_info.header', ''),
    ...content,
  };
  content = mapPDPFeatures(content);
  delete content['product_info'];
  return content;
};

// TODO need to improve this code
export const mapPDPFeatures = (content: any) => {
  const resultedContent = { ...content };
  if (resultedContent?.product_features) {
    safeGet(resultedContent, 'product_features.features', []).map((item: any) => {
      resultedContent[item._content_type_uid] = item;
    });
    resultedContent['featureFlags'] = resultedContent.product_features?.feature_flags;
    delete resultedContent['product_features'];
  }
  return resultedContent;
};

export const mapPDPContactContent = (content: any) => {
  content = mapPDPFeatures(content);
  content = {
    attribute_labels: safeGet(content, 'product_info.product_details.attribute_labels', {}),
    product_details_title: safeGet(content, 'product_info.product_details.header', ''),
    ...content,
  };
  delete content['product_info'];
  return content;
};

const unwrapAttributeLabels = (content: any) => {
  const labels = safeGet(content, 'attribute_labels', {});
  return {
    material: labels['material_label'],
    rimType: labels['rim_type_label'],
    shape: labels['shape_label'],
    color: labels['color_label'],
    size: labels['size_label'],
    gender: labels['gender_label'],
    pdRange: labels['pd_range_label'],
    progressiveFriendly: labels['progressive_friendly_label'],
    features: labels['features_label'],
  };
};

export const unwrapPDPContactContent = (content: any, SITE_MAP: ISiteMap) => {
  content = mapPDPContactContent(content);
  const productInfo = unwrapProductInfo(content);
  const our_guarantees = upwrapOurGuarantees(content, SITE_MAP);
  const opticalBenefits = upwrapOpticalBenefits(content, SITE_MAP);
  const showRebatesandOffers = content.rebates_offers;
  const product_discount = unwrapDiscount(content);
  const colorLabelMap = unwrapClAttr(content);
  const benefitTables = unwrapBenefitTables(content?.benefit_tables);
  const featureFlag = unwrapFeatureFlag(content);
  return {
    productInfo,
    our_guarantees,
    opticalBenefits,
    showRebatesandOffers,
    product_discount,
    benefitTables,
    colorLabelMap,
    featureFlag,
  };
};
export function unwrapFeatureFlag(content: any) {
  if (!content?.featureFlags) {
    return null;
  }
  return content.featureFlags;
}
export function unwrapClAttr(content: any) {
  const productRule = content?.product_rules;
  if (!productRule) return null;
  const colorAttMap = productRule[0].color?.map((item: any) => {
    return {
      label: item.target_color,
      is_default: item.is_default,
      source_color: item.source_color,
    };
  });
  return colorAttMap;
}
export function unwrapCartPageContent(content: any, SITE_MAP: ISiteMap) {
  content = mapPDPFeatures(content);
  const cartPageContent = {
    alertMessage: unwrapAlertMessage(content?.benefit_alert),
    savingsPlan: unwrapSavingsPlan(content?.protection_and_savings_plan),
    checkout: unwrapCheckout(content?.checkout, SITE_MAP),
    pageType: content?.page_type,
  };
  return {
    ...cartPageContent,
    ourGuarantees: upwrapOurGuarantees(content, SITE_MAP),
  };
}

export const unwrapFindLocation = (content: any, SITE_MAP: ISiteMap) => {
  return {
    banner: unwrapBanner(content.banner, SITE_MAP),
    card: unwrapCard(content.banner?.[0]?.card, SITE_MAP),
    breadcrumbTitle: content.page_name?.title,
  };
};

export const unwrapArticlePage = (content: any, SITE_MAP: ISiteMap) => {
  const contentBanners = content?.articles?.filter((itm: any) =>
    Object.keys(itm).includes('content_banners'),
  );
  return {
    banner: unwrapBanner(content?.banner, SITE_MAP),
    card: unwrapCard(content?.banner[0]?.card, SITE_MAP),
    breadcrumbTitle: content?.page_name.title,
    article: content?.articles,
    contentBanner:
      contentBanners && contentBanners.length > 0
        ? unwrapBanner(contentBanners?.[0]?.content_banners?.banners, SITE_MAP)
        : [],
    title: content?.page_heading,
    tiles: unwrapTiles(content?.articles?.[0]?.stack_tiles?.tile, SITE_MAP),
  };
};

export const unwrapFaqPage = (content: any, SITE_MAP: ISiteMap) => {
  const card = content?.contact_us_tiles?.card_group_tiles?.[0]?.card || [];
  return {
    Title: content?.page_heading,
    faqContent: content?.faqs?.map((item: any) => item?.faq),
    contact_us_card_tiles: content?.contact_us_tiles?.card_group_tiles,
    card_navigation: unwrapNavigation(card, SITE_MAP),
    contactus_title: content?.contact_us_tiles?.contact_us_title,
    contactus_description: content?.contact_us_tiles?.description,
  };
};

export const unwrapCancelOrderPageContent = (content: any, SITE_MAP: ISiteMap) => {
  if (!content) return;
  return {
    heading: content.heading,
    cancellation_options: content.reason_choices_for_cancellations?.map((item: any) => ({
      label: item.description,
      value: item.description,
    })),
  };
};

enum LWPageLabelCategory {
  banner = 'header_labels',
  sidebar = 'sidebar_labels',
  tag = 'tag_labels',
  main = 'body_labels',
}

enum LWPageComponentDictionary {
  bannerTitle = 'header_title_label',
  backLink = 'header_back_to_product_link',
  backToCart = 'header_back_to_cart_link',
  done = 'header_steps_completed_label',
  sidebarTitle = 'sidebar_title_label',
  subtotal = 'sidebar_subtotal_label',
  benefitDisclaimer = 'sidebar_benefit_disclaimer_label',
  addToCart = 'sidebar_add_to_cart_button',
  updateCart = 'sidebar_update_cart_button',
  saveForLater = 'sidebar_save_for_later_link',
  included = 'sidebar_item_included_label',
  edit = 'body_edit_step_button',
  recommended = 'tag_recommended_label',
  popular = 'tag_popular_label',
  viewMore = 'body_view_more_button',
  viewLess = 'body_view_less_button',
  showDemo = 'body_show_demo_button',
  next = 'body_multi_select_confirm_button',
}

enum LWStepDictionary {
  step_frame = 'frame',
  step_vision_range = 'visionRange',
  step_usage = 'usage',
  step_design = 'design',
  step_material = 'material',
  step_coating = 'coating',
  step_addons = 'addOns',
}

export const unwrapLensWizardPageContent = (
  content: any,
  SITE_MAP: ISiteMap,
): WizardPageAndStepContent => {
  const getPageLabel = (category: LWPageLabelCategory, component: LWPageComponentDictionary) => {
    return content?.[category]?.find((item: any) => item?.label_component === component)?.label;
  };

  const banner: WizardBannerContent = {
    title: getPageLabel(LWPageLabelCategory.banner, LWPageComponentDictionary.bannerTitle),
    backLink: getPageLabel(LWPageLabelCategory.banner, LWPageComponentDictionary.backLink),
    backToCart: getPageLabel(LWPageLabelCategory.banner, LWPageComponentDictionary.backToCart),
    done: getPageLabel(LWPageLabelCategory.banner, LWPageComponentDictionary.done),
  };

  const sidebar: WizardSidebarContent = {
    title: getPageLabel(LWPageLabelCategory.sidebar, LWPageComponentDictionary.sidebarTitle),
    subtotal: getPageLabel(LWPageLabelCategory.sidebar, LWPageComponentDictionary.subtotal),
    benefitDisclaimer: getPageLabel(
      LWPageLabelCategory.sidebar,
      LWPageComponentDictionary.benefitDisclaimer,
    ),
    addToCart: getPageLabel(LWPageLabelCategory.sidebar, LWPageComponentDictionary.addToCart),
    updateCart: getPageLabel(LWPageLabelCategory.sidebar, LWPageComponentDictionary.updateCart),
    saveForLater: getPageLabel(LWPageLabelCategory.sidebar, LWPageComponentDictionary.saveForLater),
    included: getPageLabel(LWPageLabelCategory.sidebar, LWPageComponentDictionary.included),
  };

  const main: WizardMainContent = {
    edit: getPageLabel(LWPageLabelCategory.main, LWPageComponentDictionary.edit),
    recommended: getPageLabel(LWPageLabelCategory.tag, LWPageComponentDictionary.recommended),
    popular: getPageLabel(LWPageLabelCategory.tag, LWPageComponentDictionary.popular),
    viewMore: getPageLabel(LWPageLabelCategory.main, LWPageComponentDictionary.viewMore),
    viewLess: getPageLabel(LWPageLabelCategory.main, LWPageComponentDictionary.viewLess),
    showDemo: getPageLabel(LWPageLabelCategory.main, LWPageComponentDictionary.showDemo),
    next: getPageLabel(LWPageLabelCategory.main, LWPageComponentDictionary.next),
  };

  const steps: WizardStepsContent = {};
  content?.wizard_steps?.forEach((item: any) => {
    const targetStep = item?.wizard_step?.[0];
    const stepKey = (LWStepDictionary as any)[targetStep?.step];
    steps[stepKey] = {
      name: targetStep?.step_labels?.step_title,
      description: targetStep?.step_labels?.step_description,
      summary_label: targetStep?.step_labels?.step_summary_label,
      options: targetStep?.step_options?.map(
        (option: { option: { uid: string }[] }) => option?.option?.[0]?.uid,
      ),
      collectiveOptions: targetStep?.step_collections?.map(
        (collection: { collection: { uid: string }[] }) => collection?.collection?.[0]?.uid,
      ),
    };
  });

  return {
    banner,
    sidebar,
    main,
    steps,
  };
};

export function unwrapOrderHistory(content: any, SITE_MAP: ISiteMap) {
  if (!content || !content.length) return;
  const orderHistory = content[0];
  const filterHeadings = orderHistory.filters.map(
    (item: { filter_heading: any }) => item.filter_heading,
  );
  const indexHeadings = orderHistory.table_indexes.map(
    (item: { index_heading: any }) => item.index_heading,
  );
  return {
    heading: orderHistory.heading,
    buttonName: orderHistory.detail_button_name,
    filterHeadings: filterHeadings,
    indexHeadings: indexHeadings,
    shipping_msgbox: orderHistory?.shipping_msgbox,
  };
}
